// src/pages/OrderPreviewPage.js (updated with dynamic pricing and NO minimum order)
import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import api from "../api/api"; // Import api for pricing config
import { 
  FaFileAlt,
  FaCalendarAlt,
  FaGraduationCap,
  FaLanguage,
  FaFileWord,
  FaBook,
  FaUniversity,
  FaQuoteRight,
  FaEdit,
  FaCheck,
  FaTimes,
  FaArrowLeft,
  FaCreditCard,
  FaInfoCircle,
  FaUser,
  FaEnvelope,
  FaExclamationTriangle,
  FaCheckCircle,
  FaPaperclip,
  FaTrash,
  FaUpload,
  FaFilePdf,
  FaFile,
  FaSave,
  FaClock,
  FaHourglassStart,
  FaCalendarDay,
  FaSpinner
} from "react-icons/fa";
import "./OrderPreviewPage.css";

const OrderPreviewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useContext(AuthContext);
  const fileInputRef = useRef(null);

  // State for dynamic pricing data
  const [pricingConfig, setPricingConfig] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(true);
  const [pricingError, setPricingError] = useState(null);

  const [orderData, setOrderData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [deadlineInput, setDeadlineInput] = useState("");

  // Academic levels array
  const academicLevels = [
    "High School", "College", "Undergraduate", "Bachelor", 
    "Master", "PhD", "Doctoral"
  ];

  // Helper functions for datetime handling
  const formatDateTimeForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return '';
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (err) {
      return '';
    }
  };

  const parseDateTimeFromInput = (value) => {
    if (!value) return null;
    try {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    } catch (err) {
      return null;
    }
  };

  const getCurrentDateTime = () => {
    const now = new Date();
    now.setHours(now.getHours() + 6); // Add 6 hours for minimum deadline
    return now;
  };

  const getMaxDateTime = () => {
    const max = new Date();
    max.setDate(max.getDate() + 30);
    return max;
  };

  const formatDateForDisplay = (date) => {
    if (!date) return '';
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (err) {
      return '';
    }
  };

  // Fetch pricing configuration from backend
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        setLoadingPricing(true);
        const response = await api.get("/administration/pricing/");
        console.log("✅ Pricing config loaded in preview:", response.data);
        setPricingConfig(response.data);
        setPricingError(null);
      } catch (err) {
        console.error("❌ Failed to fetch pricing in preview:", err);
        setPricingError("Using default pricing for calculations");
        
        // Set default pricing config as fallback
        setPricingConfig({
          academic_levels: {
            "High School": 10,
            "College": 12,
            "Undergraduate": 15,
            "Bachelor": 18,
            "Master": 22,
            "PhD": 28,
            "Doctoral": 35
          },
          deadlines: [
            { range: '0-24', percentage: 80, display_name: 'Urgent (24 hours or less)' },
            { range: '25-72', percentage: 60, display_name: 'Fast (1-3 days)' },
            { range: '73-168', percentage: 30, display_name: 'Standard (3-7 days)' },
            { range: '169-336', percentage: 10, display_name: 'Relaxed (7-14 days)' },
            { range: '337+', percentage: 0, display_name: 'Extended (14+ days)' }
          ],
          additional_services: [
            { type: 'PLAGIARISM', fee_type: 'FIXED', price: 10, name: 'Plagiarism Report', description: 'Turnitin report included' },
            { type: 'EXECUTIVE_SUMMARY', fee_type: 'FIXED', price: 15, name: 'Executive Summary', description: '1-page summary of key points' },
            { type: 'EXPERT_WRITER', fee_type: 'FIXED', price: 25, name: 'Expert Writer', description: 'Top-rated specialist in your field' }
          ],
          site_settings: {
            minimum_order: 0, // Set to 0 to avoid enforcing minimum
            enable_rush_fees: true
          }
        });
      } finally {
        setLoadingPricing(false);
      }
    };

    fetchPricing();
  }, []);

  // Initialize order data from location state or session storage
  useEffect(() => {
    const orderFromState = location.state?.orderData;
    const orderFromStorage = JSON.parse(sessionStorage.getItem('orderData'));
    
    let processedData = null;
    
    if (orderFromState) {
      processedData = {
        ...orderFromState,
        files: orderFromState.files || [],
        deadline: orderFromState.deadline ? orderFromState.deadline : null
      };
    } else if (orderFromStorage) {
      processedData = {
        ...orderFromStorage,
        files: orderFromStorage.files || [],
        deadline: orderFromStorage.deadline ? orderFromStorage.deadline : null
      };
    } else {
      navigate("/order");
      return;
    }

    setOrderData(processedData);
    setEditedData(processedData);
    
    if (processedData.deadline) {
      setDeadlineInput(formatDateTimeForInput(processedData.deadline));
    }
    
    const saveData = {
      ...processedData,
      deadline: processedData.deadline
    };
    sessionStorage.setItem('orderData', JSON.stringify(saveData));

    if (!isAuthenticated) {
      navigate("/login", { 
        state: { 
          message: "Please login to review your order",
          redirectTo: "/order-preview",
          redirectData: orderFromState || orderFromStorage
        } 
      });
    }
  }, [location.state, navigate, isAuthenticated]);

  const getRushFeeDescription = (deadline) => {
    if (!deadline || !pricingConfig?.deadlines) return "";
    
    try {
      const now = new Date();
      const deadlineDate = new Date(deadline);
      if (isNaN(deadlineDate.getTime())) return "";
      
      const hoursDifference = Math.ceil((deadlineDate - now) / (1000 * 60 * 60));
      
      if (hoursDifference <= 24) {
        const tier = pricingConfig.deadlines.find(d => d.range === '0-24');
        return tier?.display_name || "Urgent (24 hours or less)";
      } else if (hoursDifference <= 72) {
        const tier = pricingConfig.deadlines.find(d => d.range === '25-72');
        return tier?.display_name || "Fast (1-3 days)";
      } else if (hoursDifference <= 168) {
        const tier = pricingConfig.deadlines.find(d => d.range === '73-168');
        return tier?.display_name || "Standard (3-7 days)";
      }
      return "Relaxed (More than 7 days)";
    } catch (err) {
      return "";
    }
  };

  const calculateRushFee = (basePrice, deadline) => {
    if (!deadline || !pricingConfig?.site_settings?.enable_rush_fees || !pricingConfig?.deadlines) return 0;
    
    try {
      const now = new Date();
      const deadlineDate = new Date(deadline);
      if (isNaN(deadlineDate.getTime())) return 0;
      
      const hoursDifference = Math.ceil((deadlineDate - now) / (1000 * 60 * 60));
      
      let rushPercentage = 0;
      if (hoursDifference <= 24) {
        const tier = pricingConfig.deadlines.find(d => d.range === '0-24');
        rushPercentage = tier ? tier.percentage : 80;
      } else if (hoursDifference <= 72) {
        const tier = pricingConfig.deadlines.find(d => d.range === '25-72');
        rushPercentage = tier ? tier.percentage : 60;
      } else if (hoursDifference <= 168) {
        const tier = pricingConfig.deadlines.find(d => d.range === '73-168');
        rushPercentage = tier ? tier.percentage : 30;
      }
      
      return basePrice * (rushPercentage / 100);
    } catch (err) {
      return 0;
    }
  };

  const getServicePrice = (serviceType) => {
    if (!pricingConfig?.additional_services) return 0;
    
    const service = pricingConfig.additional_services.find(s => s.type === serviceType);
    if (!service) return 0;
    
    if (service.fee_type === 'FIXED') {
      return service.price || 0;
    } else if (service.fee_type === 'PER_PAGE') {
      return (service.price_per_page || 0) * (editedData?.pages || 1);
    } else if (service.fee_type === 'PERCENTAGE') {
      const basePrice = (pricingConfig.academic_levels[editedData?.academic_level] || 15) * (editedData?.pages || 1);
      return basePrice * ((service.percentage || 0) / 100);
    }
    return 0;
  };

  const getServicePriceDisplay = (serviceType) => {
    if (!pricingConfig?.additional_services) return '+$0';
    
    const service = pricingConfig.additional_services.find(s => s.type === serviceType);
    if (!service) return '+$0';
    
    if (service.fee_type === 'FIXED') {
      return `+$${service.price}`;
    } else if (service.fee_type === 'PER_PAGE') {
      return `+$${service.price_per_page}/page`;
    } else if (service.fee_type === 'PERCENTAGE') {
      return `+${service.percentage}%`;
    }
    return '+$0';
  };

  const getServiceName = (serviceType) => {
    if (!pricingConfig?.additional_services) return '';
    const service = pricingConfig.additional_services.find(s => s.type === serviceType);
    return service?.name || '';
  };

  const getServiceDescription = (serviceType) => {
    if (!pricingConfig?.additional_services) return '';
    const service = pricingConfig.additional_services.find(s => s.type === serviceType);
    return service?.description || '';
  };

  const validateAndFormatDeadline = (deadline) => {
    if (!deadline) {
      return { isValid: false, error: "Deadline is required" };
    }
    
    try {
      let dateObj;
      
      if (deadline instanceof Date) {
        dateObj = deadline;
      } else if (typeof deadline === 'string') {
        dateObj = new Date(deadline);
      } else {
        return { isValid: false, error: "Invalid deadline format" };
      }
      
      if (isNaN(dateObj.getTime())) {
        return { isValid: false, error: "Invalid date" };
      }
      
      const now = new Date();
      if (dateObj <= now) {
        return { isValid: false, error: "Deadline must be in the future" };
      }
      
      const minDeadline = new Date(now);
      minDeadline.setHours(minDeadline.getHours() + 6);
      if (dateObj < minDeadline) {
        return { isValid: false, error: "Minimum deadline is 6 hours from now" };
      }
      
      const isoString = dateObj.toISOString();
      
      return { 
        isValid: true, 
        deadline: dateObj,
        isoString: isoString 
      };
    } catch (err) {
      return { isValid: false, error: "Failed to process deadline" };
    }
  };

  const prepareOrderDataForAPI = (data) => {
    return {
      title: data.title || '',
      subject: data.subject || '',
      academic_level: data.academic_level || 'High School',
      pages: data.pages || 1,
      deadline: data.deadline ? (typeof data.deadline === 'string' ? data.deadline : data.deadline.toISOString()) : null,
      instructions: data.instructions || '',
      budget: (data.priceBreakdown?.total || 0).toFixed(2),
      language: data.language || 'English (US)',
      format: data.format || 'APA',
      spacing: data.spacing || 'Double',
      slides: data.slides || 0,
      sources: data.sources || 0,
      plagiarism_report: data.plagiarismReport || false,
      executive_summary: data.executiveSummary || false,
      expert_writer: data.expertWriter || false,
      price_breakdown: data.priceBreakdown || {}
    };
  };

  const handleEditToggle = () => {
    if (isEditing) {
      const updatedData = {
        ...editedData,
        files: [...(editedData.files || []), ...filesToUpload]
      };
      setOrderData(updatedData);
      setEditedData(updatedData);
      setFilesToUpload([]);
      sessionStorage.setItem('orderData', JSON.stringify(updatedData));
      setSuccessMessage("Order updated successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } else {
      setFilesToUpload([]);
      if (editedData.deadline) {
        setDeadlineInput(formatDateTimeForInput(editedData.deadline));
      }
    }
    setIsEditing(!isEditing);
  };

  const handleEditChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));

    if (['pages', 'deadline', 'academic_level', 'subject', 'plagiarismReport', 'executiveSummary', 'expertWriter', 'slides'].includes(field)) {
      recalculatePrice();
    }
  };

  const handleDeadlineChange = (e) => {
    const value = e.target.value;
    setDeadlineInput(value);
    
    if (!value) {
      handleEditChange('deadline', null);
      return;
    }
    
    try {
      const date = parseDateTimeFromInput(value);
      if (date && !isNaN(date.getTime())) {
        handleEditChange('deadline', date);
      } else {
        handleEditChange('deadline', null);
      }
    } catch (err) {
      handleEditChange('deadline', null);
    }
  };

  const recalculatePrice = () => {
    if (!pricingConfig || !editedData) return;

    // Get base price per page from config
    const basePricePerPage = pricingConfig.academic_levels[editedData.academic_level] || 15;
    let basePrice = basePricePerPage * editedData.pages;
    
    // Apply page count discounts if available
    if (pricingConfig.page_discounts && pricingConfig.page_discounts.length > 0) {
      const applicableDiscount = pricingConfig.page_discounts.find(
        d => editedData.pages >= d.min_pages && 
             (!d.max_pages || editedData.pages <= d.max_pages)
      );
      
      if (applicableDiscount) {
        if (applicableDiscount.modifier_type === 'discount') {
          basePrice = basePrice * (1 - applicableDiscount.modifier_value / 100);
        } else if (applicableDiscount.modifier_type === 'premium') {
          basePrice = basePrice * (1 + applicableDiscount.modifier_value / 100);
        }
      }
    }
    
    // Apply subject multipliers if available
    if (pricingConfig.subject_multipliers && editedData.subject) {
      const multiplier = pricingConfig.subject_multipliers[editedData.subject];
      if (multiplier) {
        basePrice = basePrice * multiplier;
      }
    }
    
    // Calculate rush fee
    const rushFee = calculateRushFee(basePrice, editedData.deadline);
    
    // Calculate service fees
    let serviceFee = 0;
    if (editedData.plagiarismReport) {
      serviceFee += getServicePrice('PLAGIARISM');
    }
    if (editedData.executiveSummary) {
      serviceFee += getServicePrice('EXECUTIVE_SUMMARY');
    }
    if (editedData.expertWriter) {
      serviceFee += getServicePrice('EXPERT_WRITER');
    }
    if (editedData.slides > 0) {
      serviceFee += editedData.slides * 5; // Fixed $5 per slide
    }

    // Calculate total - NO MINIMUM ORDER ENFORCEMENT
    const total = basePrice + rushFee + serviceFee;

    setEditedData(prev => ({
      ...prev,
      budget: Math.round(total),
      priceBreakdown: {
        basePrice: Math.round(basePrice),
        rushFee: Math.round(rushFee),
        serviceFee: Math.round(serviceFee),
        total: Math.round(total)
      }
    }));
  };

  const handleServiceToggle = (service) => {
    const newValue = !editedData[service];
    setEditedData(prev => ({
      ...prev,
      [service]: newValue
    }));
    
    // Recalculate price after toggling service
    setTimeout(() => recalculatePrice(), 0);
  };

  // File upload functions
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        setErrorMessage(`File "${file.name}" exceeds 10MB limit`);
        return false;
      }
      return true;
    });

    setFilesToUpload(prev => [...prev, ...validFiles]);
    e.target.value = null;
  };

  const removeUploadedFile = (index) => {
    setFilesToUpload(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingFile = (index) => {
    setEditedData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (ext === 'pdf') return <FaFilePdf className="file-icon pdf" />;
    if (['doc', 'docx'].includes(ext)) return <FaFileWord className="file-icon doc" />;
    return <FaFile className="file-icon generic" />;
  };

  const getDeadlineStatus = (deadline) => {
    if (!deadline) return { text: "Not set", className: "no-deadline" };
    
    try {
      const now = new Date();
      const deadlineDate = new Date(deadline);
      if (isNaN(deadlineDate.getTime())) {
        return { text: "Invalid date", className: "no-deadline" };
      }
      
      const hoursDifference = Math.ceil((deadlineDate - now) / (1000 * 60 * 60));
      
      if (hoursDifference <= 24) {
        return { text: "⚠️ Very Urgent", className: "very-urgent" };
      } else if (hoursDifference <= 72) {
        return { text: "Urgent", className: "urgent" };
      } else if (hoursDifference <= 168) {
        return { text: "Standard", className: "standard" };
      } else {
        return { text: "Relaxed", className: "relaxed" };
      }
    } catch (err) {
      return { text: "Error", className: "no-deadline" };
    }
  };

  const handleProceedToPayment = () => {
    setIsLoading(true);
    setErrorMessage("");
    
    const deadlineValidation = validateAndFormatDeadline(editedData.deadline);
    if (!deadlineValidation.isValid) {
      setErrorMessage(deadlineValidation.error);
      setIsLoading(false);
      return;
    }
    
    try {
      const apiData = prepareOrderDataForAPI(editedData);
      apiData.deadline = deadlineValidation.isoString;
      
      const frontendData = {
        ...editedData,
        deadline: deadlineValidation.isoString,
        budget: editedData.priceBreakdown.total,
        files: [...(editedData.files || []), ...filesToUpload]
      };

      console.log("API data prepared:", apiData);
      console.log("Frontend data:", frontendData);
      
      sessionStorage.setItem('orderData', JSON.stringify(frontendData));
      sessionStorage.setItem('orderDataAPI', JSON.stringify(apiData));
      
      setTimeout(() => {
        setIsLoading(false);
        
        const allFiles = [...(editedData.files || []), ...filesToUpload];
        console.log("All files collected for payment:", allFiles);
        
        navigate("/payment", { 
          state: { 
            orderData: frontendData,
            apiData: apiData,
            amount: frontendData.priceBreakdown.total,
            files: allFiles
          } 
        });
      }, 1000);

    } catch (err) {
      console.error("Error processing payment:", err);
      setErrorMessage("Failed to process order. Please try again.");
      setIsLoading(false);
    }
  };

  const handleBackToOrder = () => {
    navigate("/order");
  };

  // Show loading state while fetching pricing
  if (loadingPricing) {
    return (
      <div className="order-preview-container">
        <div className="order-preview-background-overlay"></div>
        <div className="order-preview-loading">
          <FaSpinner className="loading-spinner" />
          <p>Loading pricing configuration...</p>
        </div>
      </div>
    );
  }

  if (!orderData || !editedData) {
    return (
      <div className="order-preview-container">
        <div className="order-preview-background-overlay"></div>
        <div className="order-preview-loading">
          <div className="loading-spinner"></div>
          <p>Loading order details...</p>
        </div>
      </div>
    );
  }

  const dataToDisplay = isEditing ? editedData : orderData;
  const allFiles = [...(dataToDisplay.files || []), ...filesToUpload];
  const deadlineStatus = getDeadlineStatus(dataToDisplay.deadline);

  return (
    <div className="order-preview-container">
      <div className="order-preview-background-overlay"></div>
      
      <div className="order-preview-form-container">
        {/* Header */}
        <div className="order-preview-header">
          <div className="order-preview-icon-container">
            <FaFileAlt className="order-preview-main-icon" />
          </div>
          <div className="order-preview-header-content">
            <h1 className="order-preview-title">Review Your Order</h1>
            <p className="order-preview-subtitle">
              Review all details below. Make any changes if needed before proceeding to payment.
            </p>
          </div>
          
          <div className="order-preview-header-actions">
            <button 
              onClick={handleEditToggle}
              className={`order-preview-edit-btn ${isEditing ? 'editing' : ''}`}
            >
              {isEditing ? (
                <>
                  <FaCheck className="order-preview-edit-icon" />
                  Save Changes
                </>
              ) : (
                <>
                  <FaEdit className="order-preview-edit-icon" />
                  Edit Order
                </>
              )}
            </button>
            <button 
              onClick={handleBackToOrder}
              className="order-preview-back-btn"
            >
              <FaArrowLeft className="order-preview-back-icon" />
              Back to Order Form
            </button>
          </div>
        </div>

        {/* Pricing Error Warning */}
        {pricingError && (
          <div className="order-preview-warning-alert">
            <FaExclamationTriangle className="order-preview-warning-icon" />
            <span>{pricingError}</span>
          </div>
        )}

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="order-preview-success-alert">
            <FaCheckCircle className="order-preview-success-icon" />
            <span>{successMessage}</span>
          </div>
        )}

        {errorMessage && (
          <div className="order-preview-error-alert">
            <FaExclamationTriangle className="order-preview-error-icon" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* User Info Section */}
        <div className="order-preview-section user-info-section">
          <h3 className="order-preview-section-title">
            <FaUser className="order-preview-section-icon" />
            Account Information
          </h3>
          <div className="user-info-grid">
            <div className="user-info-item">
              <FaUser className="user-info-icon" />
              <div className="user-info-content">
                <label>Name</label>
                <p>{user?.username || "Not specified"}</p>
              </div>
            </div>
            <div className="user-info-item">
              <FaEnvelope className="user-info-icon" />
              <div className="user-info-content">
                <label>Email</label>
                <p>{user?.email || "Not specified"}</p>
              </div>
            </div>
            <div className="user-info-item">
              <FaGraduationCap className="user-info-icon" />
              <div className="user-info-content">
                <label>Account Type</label>
                <p className={`user-role ${user?.role?.toLowerCase()}`}>
                  {user?.role === "WRITER" ? "Writer" : 
                   user?.role === "ADMIN" ? "Administrator" : "Student/Client"}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="order-preview-grid">
          {/* Left Column - Order Details */}
          <div className="order-preview-column">
            {/* Basic Information */}
            <div className="order-preview-section">
              <h3 className="order-preview-section-title">
                <FaFileAlt className="order-preview-section-icon" />
                Order Details
              </h3>
              
              <div className="order-preview-details-grid">
                <div className="order-preview-detail">
                  <label>Paper Title</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedData.title || ''}
                      onChange={(e) => handleEditChange('title', e.target.value)}
                      className="order-preview-edit-input"
                    />
                  ) : (
                    <p>{dataToDisplay.title}</p>
                  )}
                </div>

                <div className="order-preview-detail">
                  <label>Subject Area</label>
                  {isEditing ? (
                    <select
                      value={editedData.subject || ''}
                      onChange={(e) => handleEditChange('subject', e.target.value)}
                      className="order-preview-edit-select"
                    >
                      <option value="">Select Subject</option>
                      <option value="English Literature">English Literature</option>
                      <option value="History">History</option>
                      <option value="Psychology">Psychology</option>
                      <option value="Sociology">Sociology</option>
                      <option value="Philosophy">Philosophy</option>
                      <option value="Political Science">Political Science</option>
                      <option value="Economics">Economics</option>
                      <option value="Business">Business</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Finance">Finance</option>
                      <option value="Computer Science">Computer Science</option>
                      <option value="Engineering">Engineering</option>
                      <option value="Mathematics">Mathematics</option>
                      <option value="Physics">Physics</option>
                      <option value="Chemistry">Chemistry</option>
                      <option value="Biology">Biology</option>
                      <option value="Medicine">Medicine</option>
                      <option value="Nursing">Nursing</option>
                      <option value="Law">Law</option>
                      <option value="Education">Education</option>
                      <option value="Art History">Art History</option>
                      <option value="Music Theory">Music Theory</option>
                      <option value="Other">Other</option>
                    </select>
                  ) : (
                    <p>{dataToDisplay.subject}</p>
                  )}
                </div>

                <div className="order-preview-detail">
                  <label>
                    <FaGraduationCap className="order-preview-detail-icon" />
                    Academic Level
                  </label>
                  {isEditing ? (
                    <select
                      value={editedData.academic_level || 'High School'}
                      onChange={(e) => handleEditChange('academic_level', e.target.value)}
                      className="order-preview-edit-select"
                    >
                      {academicLevels.map(level => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                  ) : (
                    <p>{dataToDisplay.academic_level}</p>
                  )}
                </div>

                <div className="order-preview-detail">
                  <label>Number of Pages</label>
                  {isEditing ? (
                    <input
                      type="number"
                      value={editedData.pages || 1}
                      onChange={(e) => handleEditChange('pages', parseInt(e.target.value) || 1)}
                      min="1"
                      max="100"
                      className="order-preview-edit-input"
                    />
                  ) : (
                    <p>{dataToDisplay.pages} pages ({dataToDisplay.pages * 275} words approx.)</p>
                  )}
                </div>
              </div>
            </div>

            {/* Deadline Section */}
            <div className="order-preview-section">
              <h3 className="order-preview-section-title">
                <FaCalendarAlt className="order-preview-section-icon" />
                Deadline
              </h3>
              
              <div className="order-preview-deadline-grid">
                {isEditing ? (
                  <div className="order-preview-detail full-width">
                    <label>
                      <FaCalendarDay className="order-preview-detail-icon" />
                      Select Deadline Date & Time
                    </label>
                    <div className="datetime-input-container">
                      <input
                        type="datetime-local"
                        value={deadlineInput}
                        onChange={handleDeadlineChange}
                        className="order-preview-edit-input datetime-input"
                        min={formatDateTimeForInput(getCurrentDateTime())}
                        max={formatDateTimeForInput(getMaxDateTime())}
                        step="900"
                      />
                      <FaCalendarAlt className="datetime-input-icon" />
                    </div>
                  </div>
                ) : (
                  <div className="order-preview-detail full-width">
                    <label>Delivery Deadline</label>
                    <div className="deadline-display-container">
                      <div className="deadline-main-info">
                        <FaClock className="deadline-icon" />
                        <div className="deadline-details">
                          <p className="deadline-date">
                            {dataToDisplay.deadline ? formatDateForDisplay(dataToDisplay.deadline) : "Not set"}
                          </p>
                          <span className={`deadline-status ${deadlineStatus.className}`}>
                            {deadlineStatus.text}
                          </span>
                        </div>
                      </div>
                      <div className="deadline-additional-info">
                        <FaInfoCircle className="deadline-info-icon" />
                        <span className="deadline-rush-fee">
                          {dataToDisplay.priceBreakdown?.rushFee > 0 ? 
                            `Rush fee applied: $${dataToDisplay.priceBreakdown.rushFee}` : 
                            "No rush fee"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {isEditing && deadlineInput && (
                <div className="order-preview-deadline-preview">
                  <FaHourglassStart className="order-preview-preview-icon" />
                  <div className="order-preview-preview-content">
                    <p><strong>Selected Deadline:</strong> {formatDateForDisplay(editedData.deadline)}</p>
                    <p className="deadline-rush-info">
                      {getRushFeeDescription(editedData.deadline)}
                      {editedData.priceBreakdown?.rushFee > 0 && (
                        <span className="rush-fee-indicator"> (Rush Fee Applied)</span>
                      )}
                    </p>
                  </div>
                </div>
              )}

              <div className="order-preview-deadline-note">
                <FaInfoCircle className="order-preview-note-icon" />
                <p>
                  Minimum deadline: 6 hours from now • Maximum deadline: 30 days from now
                </p>
              </div>
            </div>

            {/* Instructions Section */}
            <div className="order-preview-section">
              <h3 className="order-preview-section-title">Detailed Instructions</h3>
              
              <div className="order-preview-detail">
                {isEditing ? (
                  <textarea
                    value={editedData.instructions || ''}
                    onChange={(e) => handleEditChange('instructions', e.target.value)}
                    className="order-preview-edit-textarea"
                    rows={6}
                  />
                ) : (
                  <div className="instructions-display">
                    {dataToDisplay.instructions.split('\n').map((line, index) => (
                      <p key={index}>{line || <br />}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Supporting Files Section */}
            <div className="order-preview-section">
              <h3 className="order-preview-section-title">
                <FaPaperclip className="order-preview-section-icon" />
                Supporting Files
              </h3>
              
              {isEditing ? (
                <>
                  <div className="order-preview-file-upload">
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileUpload}
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                      className="order-preview-file-input"
                      style={{ display: 'none' }}
                    />
                    
                    <button
                      type="button"
                      onClick={triggerFileInput}
                      className="order-preview-file-upload-btn"
                    >
                      <FaUpload className="order-preview-file-upload-icon" />
                      Add More Files
                    </button>
                    
                    <small className="order-preview-file-hint">
                      PDF, DOC, DOCX, TXT, JPG, PNG (Max 10MB each)
                    </small>
                  </div>

                  {(allFiles.length > 0 || filesToUpload.length > 0) && (
                    <div className="order-preview-file-list">
                      <h4>Attached Files:</h4>
                      <div className="order-preview-files-container">
                        {(dataToDisplay.files || []).map((file, index) => (
                          <div key={`existing-${index}`} className="order-preview-file-item">
                            {getFileIcon(file.name)}
                            <div className="order-preview-file-info">
                              <span className="order-preview-file-name">{file.name}</span>
                              <span className="order-preview-file-size">
                                {formatFileSize(file.size)}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeExistingFile(index)}
                              className="order-preview-file-remove"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        ))}
                        
                        {filesToUpload.map((file, index) => (
                          <div key={`new-${index}`} className="order-preview-file-item new">
                            {getFileIcon(file.name)}
                            <div className="order-preview-file-info">
                              <span className="order-preview-file-name">{file.name}</span>
                              <span className="order-preview-file-size">
                                {formatFileSize(file.size)}
                              </span>
                              <span className="order-preview-file-status">New</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeUploadedFile(index)}
                              className="order-preview-file-remove"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="order-preview-file-display">
                  {allFiles.length > 0 ? (
                    <div className="order-preview-files-container">
                      {allFiles.map((file, index) => (
                        <div key={index} className="order-preview-file-item">
                          {getFileIcon(file.name)}
                          <div className="order-preview-file-info">
                            <span className="order-preview-file-name">{file.name}</span>
                            <span className="order-preview-file-size">
                              {formatFileSize(file.size)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="order-preview-no-files">
                      No supporting files attached
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Requirements & Pricing */}
          <div className="order-preview-column">
            {/* Formatting Requirements */}
            <div className="order-preview-section">
              <h3 className="order-preview-section-title">
                <FaBook className="order-preview-section-icon" />
                Formatting Requirements
              </h3>
              
              <div className="order-preview-requirements-grid">
                {['language', 'format', 'spacing', 'sources', 'slides'].map((field) => (
                  <div className="order-preview-detail" key={field}>
                    <label>
                      {field === 'language' && <><FaLanguage className="order-preview-detail-icon" /> Language</>}
                      {field === 'format' && 'Format Style'}
                      {field === 'spacing' && 'Spacing'}
                      {field === 'sources' && 'Number of Sources'}
                      {field === 'slides' && 'Slides (if presentation)'}
                    </label>
                    {isEditing ? (
                      field === 'language' || field === 'format' || field === 'spacing' ? (
                        <select
                          value={editedData[field] || ''}
                          onChange={(e) => handleEditChange(field, e.target.value)}
                          className="order-preview-edit-select"
                        >
                          {field === 'language' && (
                            <>
                              <option value="English (US)">English (US)</option>
                              <option value="English (UK)">English (UK)</option>
                              <option value="Spanish">Spanish</option>
                              <option value="French">French</option>
                              <option value="German">German</option>
                              <option value="Italian">Italian</option>
                              <option value="Portuguese">Portuguese</option>
                              <option value="Chinese">Chinese</option>
                              <option value="Japanese">Japanese</option>
                              <option value="Arabic">Arabic</option>
                              <option value="Russian">Russian</option>
                              <option value="Other">Other</option>
                            </>
                          )}
                          {field === 'format' && (
                            <>
                              <option value="APA">APA</option>
                              <option value="MLA">MLA</option>
                              <option value="Chicago">Chicago</option>
                              <option value="Harvard">Harvard</option>
                              <option value="IEEE">IEEE</option>
                              <option value="Other">Other</option>
                            </>
                          )}
                          {field === 'spacing' && (
                            <>
                              <option value="Single">Single</option>
                              <option value="Double">Double</option>
                              <option value="1.5">1.5</option>
                            </>
                          )}
                        </select>
                      ) : (
                        <input
                          type="number"
                          value={editedData[field] || 0}
                          onChange={(e) => handleEditChange(field, parseInt(e.target.value) || 0)}
                          min="0"
                          max={field === 'sources' ? "50" : "50"}
                          className="order-preview-edit-input"
                        />
                      )
                    ) : (
                      <p>
                        {field === 'sources' || field === 'slides' 
                          ? `${dataToDisplay[field] || 0}` 
                          : dataToDisplay[field]}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Services - WITH DYNAMIC PRICING */}
            <div className="order-preview-section">
              <h3 className="order-preview-section-title">
                <FaUniversity className="order-preview-section-icon" />
                Additional Services
              </h3>
              
              <div className="order-preview-services-grid">
                {/* Plagiarism Report */}
                <div className="order-preview-service-item">
                  <label className="order-preview-service-label">
                    {isEditing ? (
                      <input
                        type="checkbox"
                        checked={editedData.plagiarismReport || false}
                        onChange={() => handleServiceToggle('plagiarismReport')}
                        className="order-preview-checkbox"
                      />
                    ) : (
                      <div className={`order-preview-service-check ${dataToDisplay.plagiarismReport ? 'checked' : ''}`}>
                        {dataToDisplay.plagiarismReport ? <FaCheck /> : <FaTimes />}
                      </div>
                    )}
                    <span className="order-preview-service-text">
                      <strong>{getServiceName('PLAGIARISM') || 'Plagiarism Report'}</strong>
                      <small>{getServiceDescription('PLAGIARISM') || 'Turnitin report included'}</small>
                    </span>
                    <span className="order-preview-service-price">
                      {getServicePriceDisplay('PLAGIARISM') || '+$10'}
                    </span>
                  </label>
                </div>

                {/* Executive Summary */}
                <div className="order-preview-service-item">
                  <label className="order-preview-service-label">
                    {isEditing ? (
                      <input
                        type="checkbox"
                        checked={editedData.executiveSummary || false}
                        onChange={() => handleServiceToggle('executiveSummary')}
                        className="order-preview-checkbox"
                      />
                    ) : (
                      <div className={`order-preview-service-check ${dataToDisplay.executiveSummary ? 'checked' : ''}`}>
                        {dataToDisplay.executiveSummary ? <FaCheck /> : <FaTimes />}
                      </div>
                    )}
                    <span className="order-preview-service-text">
                      <strong>{getServiceName('EXECUTIVE_SUMMARY') || 'Executive Summary'}</strong>
                      <small>{getServiceDescription('EXECUTIVE_SUMMARY') || '1-page summary of key points'}</small>
                    </span>
                    <span className="order-preview-service-price">
                      {getServicePriceDisplay('EXECUTIVE_SUMMARY') || '+$15'}
                    </span>
                  </label>
                </div>

                {/* Expert Writer */}
                <div className="order-preview-service-item">
                  <label className="order-preview-service-label">
                    {isEditing ? (
                      <input
                        type="checkbox"
                        checked={editedData.expertWriter || false}
                        onChange={() => handleServiceToggle('expertWriter')}
                        className="order-preview-checkbox"
                      />
                    ) : (
                      <div className={`order-preview-service-check ${dataToDisplay.expertWriter ? 'checked' : ''}`}>
                        {dataToDisplay.expertWriter ? <FaCheck /> : <FaTimes />}
                      </div>
                    )}
                    <span className="order-preview-service-text">
                      <strong>{getServiceName('EXPERT_WRITER') || 'Expert Writer'}</strong>
                      <small>{getServiceDescription('EXPERT_WRITER') || 'Top-rated specialist in your field'}</small>
                    </span>
                    <span className="order-preview-service-price">
                      {getServicePriceDisplay('EXPERT_WRITER') || '+$25'}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Price Summary */}
            <div className="order-preview-price-summary">
              <h3 className="order-preview-price-title">
                <FaQuoteRight className="order-preview-price-icon" />
                Price Summary
              </h3>
              
              <div className="order-preview-price-breakdown">
                <div className="order-preview-price-row">
                  <span>Base Price ({dataToDisplay.pages} pages)</span>
                  <span>${dataToDisplay.priceBreakdown?.basePrice || 0}</span>
                </div>
                
                {dataToDisplay.priceBreakdown?.rushFee > 0 && (
                  <div className="order-preview-price-row order-preview-price-rush">
                    <span>Rush Fee ({getRushFeeDescription(dataToDisplay.deadline)})</span>
                    <span>+${dataToDisplay.priceBreakdown.rushFee}</span>
                  </div>
                )}
                
                {dataToDisplay.priceBreakdown?.serviceFee > 0 && (
                  <div className="order-preview-price-row">
                    <span>Additional Services</span>
                    <span>+${dataToDisplay.priceBreakdown.serviceFee}</span>
                  </div>
                )}
                
                <div className="order-preview-price-row order-preview-price-total">
                  <strong>Total Amount</strong>
                  <strong>${dataToDisplay.priceBreakdown?.total || 0}</strong>
                </div>
              </div>

              {dataToDisplay.deadline && (
                <div className="order-preview-deadline-summary">
                  <p>
                    <strong>Deadline:</strong> {formatDateForDisplay(dataToDisplay.deadline)}
                  </p>
                </div>
              )}

              <div className="order-preview-price-note">
                <FaInfoCircle className="order-preview-note-icon" />
                <p>This is the final price. You'll be redirected to our secure payment gateway.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Edit Mode Warning */}
        {isEditing && (
          <div className="order-preview-edit-warning">
            <FaExclamationTriangle className="order-preview-edit-warning-icon" />
            <div className="order-preview-edit-warning-content">
              <h4>Edit Mode Active</h4>
              <p>You're currently editing your order. Remember to save your changes before proceeding to payment.</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="order-preview-actions">
          <button 
            onClick={handleBackToOrder}
            className="order-preview-back-action-btn"
          >
            <FaArrowLeft className="order-preview-action-icon" />
            Back to Order Form
          </button>
          
          <div className="order-preview-main-actions">
            <button 
              onClick={handleEditToggle}
              className={`order-preview-bottom-edit-btn ${isEditing ? 'editing' : ''}`}
            >
              {isEditing ? (
                <>
                  <FaSave className="order-preview-bottom-edit-icon" />
                  Save & Continue
                </>
              ) : (
                <>
                  <FaEdit className="order-preview-bottom-edit-icon" />
                  Edit Order
                </>
              )}
            </button>
            
            <button 
              onClick={handleProceedToPayment}
              className="order-preview-payment-btn"
              disabled={isLoading || isEditing || !dataToDisplay.deadline}
            >
              {isLoading ? (
                <>
                  <span className="order-preview-spinner"></span>
                  Processing...
                </>
              ) : (
                <>
                  <FaCreditCard className="order-preview-action-icon" />
                  Proceed to Secure Payment
                </>
              )}
            </button>
          </div>
        </div>

        {/* Important Notes */}
        <div className="order-preview-notes">
          <div className="order-preview-note-item">
            <FaInfoCircle className="order-preview-note-item-icon" />
            <p><strong>Quality Guarantee:</strong> Your paper will be written from scratch and will pass plagiarism checks.</p>
          </div>
          <div className="order-preview-note-item">
            <FaInfoCircle className="order-preview-note-item-icon" />
            <p><strong>Revision Policy:</strong> You're entitled to free revisions within 14 days of delivery.</p>
          </div>
          <div className="order-preview-note-item">
            <FaInfoCircle className="order-preview-note-item-icon" />
            <p><strong>Confidentiality:</strong> Your personal information and order details are kept strictly confidential.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderPreviewPage;